import { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { useAuth } from "@/lib/authContext";
import { useFan } from "@/lib/fanContext";
import { toast } from "sonner";
import { CalendarDays, MapPin, Ticket, Users, ExternalLink, ArrowUpRight, Bookmark, Check, Heart, Building2 } from "lucide-react";
import { formatDateLong, formatPrice } from "@/lib/format";

export default function EventPage() {
    const { id } = useParams();
    const nav = useNavigate();
    const [params, setParams] = useSearchParams();
    const { user } = useAuth();
    const { fan, isFollowing, follow, unfollow, isFollowingVenue, followVenue, unfollowVenue, isRegistered, isSaved, isAttended, register, cancelRegistration, save, unsave } = useFan();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setLoading(true);
        endpoints.getEvent(id).then((d) => {
            setData(d);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [id]);

    // Auto-follow the venue if we returned from sign-in with ?follow_venue=1
    useEffect(() => {
        if (!fan || !data?.venue?.id) return;
        if (params.get("follow_venue") !== "1") return;
        if (isFollowingVenue(data.venue.id)) {
            const next = new URLSearchParams(params); next.delete("follow_venue"); setParams(next, { replace: true });
            return;
        }
        (async () => {
            try {
                await followVenue(data.venue.id, "event_page", id);
                toast.success(`Following ${data.venue.name}`);
            } catch { /* ignore */ }
            const next = new URLSearchParams(params); next.delete("follow_venue"); setParams(next, { replace: true });
        })();
    }, [fan, data?.venue?.id, id, params]);

    const toggleFollowVenue = async () => {
        if (!data?.venue) return;
        const vid = data.venue.id;
        if (!user || !fan) {
            nav(`/signin?next=${encodeURIComponent(`/events/${id}?follow_venue=1`)}`);
            return;
        }
        setBusy(true);
        try {
            if (isFollowingVenue(vid)) {
                await unfollowVenue(vid);
                toast(`Unfollowed ${data.venue.name}`);
            } else {
                await followVenue(vid, "event_page", id);
                toast.success(`Following ${data.venue.name}`);
            }
        } catch {
            toast.error("Could not update follow.");
        } finally { setBusy(false); }
    };

    const registered = isRegistered(id);
    const saved = isSaved(id);
    const attended = isAttended(id);

    const handleRegister = async () => {
        if (!fan) { toast.error("Switch role to Fan to register"); return; }
        setBusy(true);
        try {
            await register(id);
            setData((d) => (d ? { ...d, event: { ...d.event, registrations: (d.event.registrations || 0) + 1 } } : d));
            toast.success("You're on the list.");
        } catch {
            toast.error("Registration failed. Try again.");
        } finally {
            setBusy(false);
        }
    };

    const handleCancel = async () => {
        if (!window.confirm("Cancel your registration for this event? Your Event Pass will no longer be valid.")) return;
        setBusy(true);
        try {
            await cancelRegistration(id);
            setData((d) => (d ? { ...d, event: { ...d.event, registrations: Math.max(0, (d.event.registrations || 1) - 1) } } : d));
            toast.success("Your registration has been cancelled.");
        } catch (e) {
            const msg = e?.response?.data?.detail || "Cancellation failed. Try again.";
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    const toggleFollowArtist = async () => {
        if (!fan) { toast.error("Switch role to Fan to follow"); return; }
        if (!data?.artist) return;
        const aid = data.artist.id;
        setBusy(true);
        try {
            if (isFollowing(aid)) {
                await unfollow(aid);
                setData((d) => (d ? { ...d, artist: { ...d.artist, followers: Math.max(0, (d.artist.followers || 1) - 1) } } : d));
                toast(`Unfollowed ${data.artist.name}`);
            } else {
                await follow(aid, "event_page", id);
                setData((d) => (d ? { ...d, artist: { ...d.artist, followers: (d.artist.followers || 0) + 1 } } : d));
                toast.success(`Following ${data.artist.name}`);
            }
        } catch {
            toast.error("Could not update follow.");
        } finally {
            setBusy(false);
        }
    };

    const toggleSave = async () => {
        if (!fan) { toast.error("Switch role to Fan to save"); return; }
        setBusy(true);
        try {
            if (saved) {
                await unsave(id);
                toast("Removed from saved");
            } else {
                await save(id);
                toast.success("Saved to your passport");
            }
        } catch {
            toast.error("Could not update");
        } finally {
            setBusy(false);
        }
    };

    if (loading) return <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-24 font-mono text-sm text-[#666]" data-testid="event-loading">Loading…</div>;
    if (!data) return null;

    const e = data.event;
    const capacityLeft = Math.max(0, e.capacity - (e.registrations || 0));
    const soldRatio = Math.min(100, Math.round(((e.registrations || 0) / e.capacity) * 100));

    return (
        <div data-testid="event-page" className="pm-fade">
            <section className="relative border-b border-[#2a2a2a]">
                <div className="relative h-[45vh] md:h-[60vh] overflow-hidden">
                    <img src={e.image_url} alt="" className="absolute inset-0 w-full h-full object-cover grayscale" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-[#0a0a0a]/20" />
                </div>
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 -mt-32 md:-mt-40 relative z-10 pb-8">
                    <Link to={`/artists/${e.artist_id}`} className="inline-block text-[10px] font-mono uppercase tracking-widest text-[#ff4400] hover:underline" data-testid="event-artist-link">
                        {e.artist_name} →
                    </Link>
                    <h1 className="mt-2 font-display font-black uppercase tracking-tighter text-4xl md:text-6xl lg:text-7xl leading-[0.9]" data-testid="event-name">
                        {e.name}
                    </h1>
                    <div className="mt-4 flex flex-wrap gap-4 text-sm text-[#a3a3a3]">
                        <span className="flex items-center gap-1"><CalendarDays className="w-4 h-4" /> {formatDateLong(e.date)} · {e.start_time}–{e.end_time}</span>
                        <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> <Link to={`/venues/${e.venue_id}`} className="hover:text-[#ff4400] hover:underline" data-testid="event-venue-link">{e.venue_name}</Link> · {e.city}, {e.country}</span>
                        <span className="flex items-center gap-1"><Users className="w-4 h-4" /> Capacity {e.capacity}</span>
                    </div>
                </div>
            </section>

            <section className="border-b border-[#2a2a2a]">
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-10 md:py-14 grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="md:col-span-2">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">About</div>
                        <p className="mt-3 text-lg leading-relaxed">{e.description}</p>

                        {(e.payment_instructions || e.external_payment_link) && (
                            <div className="mt-8 border border-[#2a2a2a] p-5">
                                <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">Payment instructions</div>
                                <p className="mt-2 text-sm text-[#f4f4f0]">{e.payment_instructions}</p>
                                {e.external_payment_link && (
                                    <a href={e.external_payment_link} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 text-xs font-mono uppercase text-[#ff4400] hover:underline">
                                        External payment link <ExternalLink className="w-3 h-3" />
                                    </a>
                                )}
                                <div className="mt-3 text-[10px] font-mono uppercase text-[#666]">
                                    Project Mint does not process payments.
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="border border-[#2a2a2a] p-5 h-fit">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Access tiers</div>
                        {e.is_free ? (
                            <div className="mt-3 font-display font-black text-3xl uppercase text-[#00e676]">Free entry</div>
                        ) : e.ticket_categories.length === 0 ? (
                            <div className="mt-3 text-sm text-[#a3a3a3]">To be announced.</div>
                        ) : (
                            <div className="mt-3 space-y-2">
                                {e.ticket_categories.map((c) => (
                                    <div key={c.id} className="border border-[#2a2a2a] p-3 flex items-center justify-between" data-testid={`ticket-cat-${c.id}`}>
                                        <div>
                                            <div className="font-display font-bold uppercase text-sm">{c.name}</div>
                                            {c.description && <div className="text-[10px] text-[#a3a3a3] mt-1">{c.description}</div>}
                                        </div>
                                        <div className="font-display font-black text-lg">{formatPrice(c.price, c.currency)}</div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="mt-5">
                            <div className="text-[10px] font-mono uppercase text-[#666] mb-1">
                                {e.registrations || 0} registered · {capacityLeft} spots left
                            </div>
                            <div className="h-1 bg-[#2a2a2a] w-full">
                                <div className="h-full bg-[#ff4400]" style={{ width: `${soldRatio}%` }} />
                            </div>
                        </div>

                        {e.is_pay_at_door && (
                            <div className="mt-5 border border-[#ffea00] p-3 text-[11px] font-mono uppercase text-[#ffea00]">
                                Pay at door
                            </div>
                        )}

                        {/* Fan actions */}
                        <div className="mt-5 space-y-2">
                            {attended ? (
                                <div className="pm-btn w-full text-[#00e676] border-[#00e676] pointer-events-none" data-testid="event-attended-badge">
                                    <Check className="w-4 h-4 mr-2" /> You attended
                                </div>
                            ) : (
                                <button
                                    onClick={handleRegister}
                                    disabled={busy || registered}
                                    className="pm-btn pm-btn-primary w-full"
                                    data-testid="event-register-btn"
                                >
                                    <Ticket className="w-4 h-4 mr-2" />
                                    {registered ? "Registered" : "Register / Attend"}
                                </button>
                            )}
                            {registered && !attended && data?.event?.status === "upcoming" && (
                                <div className="pt-1 text-center">
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        disabled={busy}
                                        className="text-[10px] font-mono uppercase tracking-widest text-[#666] hover:text-[#ff3333] underline underline-offset-4 transition-colors"
                                        data-testid="event-cancel-registration-btn"
                                    >
                                        Cancel registration
                                    </button>
                                </div>
                            )}
                            {!attended && (
                                <button
                                    onClick={toggleSave}
                                    disabled={busy || registered}
                                    className="pm-btn w-full"
                                    data-testid="event-save-btn"
                                    aria-disabled={registered}
                                    title={registered ? "You're already registered" : ""}
                                >
                                    <Bookmark className="w-4 h-4 mr-2" fill={saved ? "currentColor" : "none"} />
                                    {registered ? "Registered — you're going" : saved ? "Saved · Interested" : "Save / Interested"}
                                </button>
                            )}
                            {fan && (
                                <Link to="/fan/events" className="block text-center text-[10px] font-mono uppercase text-[#a3a3a3] hover:text-[#ff4400]" data-testid="event-view-passport-link">
                                    View in your passport →
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {data.venue && (
                <section className="border-b border-[#2a2a2a]">
                    <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-10 grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="flex flex-col">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Venue</div>
                            <div className="mt-1 font-display font-black uppercase text-2xl md:text-3xl"><Link to={`/venues/${data.venue.id}`} className="hover:text-[#ff4400]" data-testid="event-venue-body-link">{data.venue.name}</Link></div>
                            <div className="mt-2 text-sm text-[#a3a3a3]">{data.venue.address}</div>
                            <div className="mt-2 text-xs font-mono uppercase text-[#666]">Capacity {data.venue.capacity}</div>
                            <div className="mt-4">
                                <button
                                    onClick={toggleFollowVenue}
                                    disabled={busy}
                                    className={`pm-btn text-xs ${isFollowingVenue(data.venue.id) ? "" : "border-[#f4f4f0]"}`}
                                    data-testid="event-follow-venue-btn"
                                    title="Follow this venue for upcoming shows"
                                >
                                    <Building2 className="w-3 h-3 mr-2" />
                                    {isFollowingVenue(data.venue.id) ? "Following venue" : "Follow venue"}
                                </button>
                            </div>
                        </div>
                        <div className="relative aspect-[16/9]">
                            <img src={data.venue.image_url} alt="" className="absolute inset-0 w-full h-full object-cover grayscale" />
                        </div>
                    </div>
                </section>
            )}

            {data.artist && (
                <section>
                    <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-10">
                        <div className="flex items-center gap-3 border border-[#2a2a2a] p-5" data-testid="event-artist-block">
                            <Link to={`/artists/${data.artist.id}`} className="flex items-center gap-3 flex-1 min-w-0 group">
                                <img src={data.artist.image_url} alt="" className="w-16 h-16 object-cover grayscale group-hover:grayscale-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-[10px] font-mono uppercase text-[#666]">Performing</div>
                                    <div className="font-display font-black uppercase text-lg group-hover:text-[#ff4400] truncate">{data.artist.name}</div>
                                    <div className="text-xs text-[#a3a3a3] truncate">{data.artist.genre} · {data.artist.city}, {data.artist.country}</div>
                                </div>
                            </Link>
                            <button
                                onClick={toggleFollowArtist}
                                disabled={busy}
                                className={`pm-btn text-xs ${isFollowing(data.artist.id) ? "" : "pm-btn-primary"}`}
                                data-testid="event-follow-artist-btn"
                            >
                                <Heart className="w-3 h-3 mr-1" fill={isFollowing(data.artist.id) ? "currentColor" : "none"} />
                                {isFollowing(data.artist.id) ? "Following" : "Follow"}
                            </button>
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}
